
require("./../../node_modules/@module-federation/nextjs-mf/src/include-defaults.js");
import Head from 'next/head';
import styled, { css } from 'styled-components';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { BrandedContentContainer } from 'homepage/components/brandView/BrandedContentContainer';
import { BRAND_FEED_SLUG, SECTION_TYPES } from 'homepage/enums/contentConstants';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { ContentIterator } from 'homepage/components/ContentIterator';
import { getDiscoverFeed } from 'server/controllers/cms-service';
import { sneakersRequest } from 'server/services/sneakers/sneakersRequest';
import { customCache } from 'homepage/utils/HomeTabCustomCache';
import { FeatureFlags } from 'featureFlags/constants';
import { getOrgSchema } from 'homepage/utils/getOrgSchema';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { fetchFeed } from 'homepage/services/homepageApi';
import { GoatLogo } from 'shared/icons/GoatLogo';
import { homepageDataCache } from 'homepage/utils/HomepageDataAndPositionCache';
import { HomeTab } from 'homepage/components/HomeTab';
import { IBreadcrumb } from 'shared/types/Breadcrumbs';
import { IFeed, IFeedBrandFeed } from 'homepage/types/Feed';
import { isSearchCrawler } from 'shared/utils/isSearchCrawler';
import { pickThemeByColorContrast } from 'shared/utils/pickThemeByColorContrast';
import { TAB_TITLES } from 'homepage/enums/tabConstants';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';
import {
  setActiveTab,
  setDefaultFeedData,
  setIsLoading,
  setVisitedByBackButton,
} from 'store/actions/homepageActions';
import { setIsHeaderVisible } from 'store/slice/pageLayoutSlice';
import { variableFontRegular } from 'styles/theme/text';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';
import { t } from 'localization/utils/t';
import { useLocation } from 'shared/hooks/useLocation';
import { useIsMounted } from 'shared/hooks/useIsMounted';
import { useFetchUserSegmentsQuery } from 'store/api/consumerSegmentAccess';
import { getTranslations } from 'shared/utils/getTranslations';
import { useConstructorUserParams } from 'shared/hooks/useConstructorUserParams';

const TP = 'pages.index';

interface IHome {
  breadcrumbs: IBreadcrumb;
  discoverTabFeed: IFeed;
  isCrawler?: boolean;
}

const Home = ({ breadcrumbs, discoverTabFeed, isCrawler = false }: IHome) => {
  const tabTitles = getTranslations(TAB_TITLES);
  const storeDispatch = useStoreDispatch();
  const router = useRouter();
  const isConsumerSegmentAccessEnabled = useFeatureFlag(
    FeatureFlags.TEMP_WEB_ENABLE_CONSUMER_SEGMENT_ACCESS_CALL,
  );

  const showDiscoverTabByDefault =
    useFeatureFlag(FeatureFlags.WEB_ENABLE_DEFAULT_HOME_TAB_DISCOVER) || isCrawler;
  const showBrandTabByDefault =
    useFeatureFlag(FeatureFlags.WEB_ENABLE_DEFAULT_HOME_TAB_BRAND) && !isCrawler;
  const name = breadcrumbs?.name || t(`${TP}.breadCrumbBrand`, 'Brand');
  const isBrandTabEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_HOMEPAGE_BRAND_TAB);
  const isHalfTabEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_BRAND_HALF_TAB);

  const constructorUserParams = useConstructorUserParams();
  const [brandFeed, setBrandFeed] = useState<IFeedBrandFeed | null>(null);
  const [discoverFeed, setDiscoverFeed] = useState(null);
  const [initialScrollPosition, setInitialScrollPosition] = useState(0);
  const [tabAndTabFontColor, setTabAndTabFontColor] = useState({
    tabColor: null,
    tabFontColor: null,
  });

  const { activeTab, isLoading, visitedByBackButton } = useStoreSelector((state) => state.homepage);
  const { isMobile, isTablet, isDesktop } = useWindowSize();
  const { selectedCountryCode: countryCode } = useLocation();
  const { data, isLoading: isLoadingUserSegments } = useFetchUserSegmentsQuery(
    {
      countryCode,
    },
    { skip: !isConsumerSegmentAccessEnabled },
  );
  const isMounted = useIsMounted();

  const tabStack = [{ title: name }];

  // if showDiscoverTabByDefault - Discover is the first tab and default selected, else Brands is the first tab.
  const HOMEPAGE_TABS = [
    { tabName: tabTitles.DISCOVER, tabTitle: tabTitles.DISCOVER },
    { tabName: tabTitles.BRAND, tabTitle: name },
  ];
  const HOMEPAGE_TABS_ORDERED = showDiscoverTabByDefault ? HOMEPAGE_TABS : HOMEPAGE_TABS.reverse();
  const segmentIds = data?.userSegmentsList?.map((segment) => segment.id);

  const handleTabClick = (tab, tabTitle) => {
    storeDispatch(setActiveTab(tabTitle));
    sendTrackingEvent('DRAWER_EXPAND', {
      drawer_name: tabTitle?.toLowerCase(),
    });
  };

  useEffect(() => {
    const getBrandFeed = async () => {
      try {
        if (isMounted() && !isLoadingUserSegments) {
          let response = await fetchFeed(BRAND_FEED_SLUG, segmentIds);
          setBrandFeed({ ...response });
          response = null;
        }
      } catch (error) {
        bugsnag(error, { context: 'Fetching Brand Feed' });
      }
    };

    if (isBrandTabEnabled) {
      void getBrandFeed();
    }
  }, [isLoadingUserSegments]);

  useEffect(() => {
    if (!activeTab && showDiscoverTabByDefault) {
      storeDispatch(setActiveTab(tabTitles.DISCOVER));
    } else if (!activeTab && showBrandTabByDefault && !visitedByBackButton) {
      storeDispatch(setActiveTab(name));
    }

    if (isMobile) {
      storeDispatch(setIsHeaderVisible(true));
    }
  }, []);

  useEffect(() => {
    try {
      if (visitedByBackButton) {
        const { cachedScrollPosition, cachedFeed } = homepageDataCache.read();
        if (cachedFeed?.uuid || cachedFeed?.id) {
          storeDispatch(setDefaultFeedData(cachedFeed));
          setDiscoverFeed({ ...cachedFeed, cachedFeed: true });
          storeDispatch(setIsLoading(false));
        }
        if (typeof cachedScrollPosition === 'number') {
          setInitialScrollPosition(cachedScrollPosition);
        }
      } else {
        storeDispatch(setDefaultFeedData(discoverTabFeed));
      }
    } catch (error) {
      bugsnag(error, { context: 'visitedByBackButton on Homepage' });
    }
  }, [visitedByBackButton]);

  useEffect(() => {
    if (
      visitedByBackButton &&
      !isLoading &&
      initialScrollPosition !== 0 &&
      discoverFeed?.cachedFeed
    ) {
      setTimeout(() => {
        storeDispatch(setVisitedByBackButton(false));
        window.scrollTo(0, initialScrollPosition);
      }, 200);
    }
  }, [visitedByBackButton, isLoading, discoverFeed]);

  useEffect(() => {
    const handleRouteChange = () => {
      try {
        // if the current tab is Discover and the user navigates away from homepage, we want to write to customCache
        // so that if the user navigates back to homepage via back button, they are shown the Discover tab
        if (activeTab === tabTitles.DISCOVER) {
          customCache.write({ tabName: tabTitles.DISCOVER, scrollPosition: 0 }, tabTitles.DISCOVER);
        }
      } catch (error) {
        bugsnag(error, { context: 'routeChangeStart on Homepage Tab' });
      }
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [activeTab]);

  useEffect(() => {
    if (brandFeed?.sections?.[0]?.type === SECTION_TYPES.SPACE_ARTICLE_SECTION && isMounted()) {
      const backgroundColor = brandFeed?.sections?.[0]?.articles?.[0]?.sections?.[0]?.bgColor;
      const theme = pickThemeByColorContrast(backgroundColor);
      const textColor = theme === 'dark' ? 'white' : 'black';
      setTabAndTabFontColor({ tabColor: backgroundColor, tabFontColor: textColor });
    }
  }, [brandFeed]);

  const currentDiscoverFeed = discoverFeed?.cachedFeed ? discoverFeed : discoverTabFeed;

  return (
    <>
      <Head>
        <title key="title">{t(`${TP}.pageTitle`, 'GOAT: Sneakers, Apparel, Accessories')}</title>
        <meta
          key="description"
          name="description"
          content={t(
            `${TP}.metaDescription`,
            `The global platform for the greatest products from the past, present and future. Shop new and used sneakers, apparel and accessories.`,
          )}
        />
        <meta
          key="og:description"
          property="og:description"
          content={t(
            `${TP}.ogDescription`,
            `The global platform for the greatest products from the past, present and future. Shop new and used sneakers, apparel and accessories.`,
          )}
        />
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: getOrgSchema(),
          }}
        />
      </Head>
      <Home.Container $minHeight={initialScrollPosition}>
        {isMobile && (
          <div>
            {isBrandTabEnabled && (
              <Home.TabWrapper>
                {HOMEPAGE_TABS_ORDERED.map(
                  ({ tabName, tabTitle }: { tabName: string; tabTitle: string }) => {
                    return (
                      <Home.Tab
                        key={`${tabTitle}_mobile`}
                        $isSelected={activeTab === tabTitle}
                        onClick={() => handleTabClick(tabName, tabTitle)}
                        data-qa={`homepage_${tabName?.toLowerCase()}_tab`}
                      >
                        {tabTitle}
                      </Home.Tab>
                    );
                  },
                )}
              </Home.TabWrapper>
            )}
            {activeTab === tabTitles.DISCOVER && (
              <ContentIterator
                constructorUserParams={constructorUserParams}
                draftId={currentDiscoverFeed?.draftId}
                draftUuid={currentDiscoverFeed?.draftUuid}
                sections={currentDiscoverFeed.sections}
                userSegmentIds={segmentIds || []}
              />
            )}
            {activeTab !== tabTitles.DISCOVER && (
              <BrandedContentContainer
                heroes={brandFeed?.sections || []}
                draftId={brandFeed?.draftId}
                draftUuid={brandFeed?.draftUuid}
                drawerName={name}
                noTopMargin
                isVisible={activeTab === name}
              />
            )}
          </div>
        )}

        {(isTablet || isDesktop) && (
          <>
            {discoverFeed || discoverTabFeed ? (
              <ContentIterator
                constructorUserParams={constructorUserParams}
                draftId={currentDiscoverFeed?.draftId}
                draftUuid={currentDiscoverFeed?.draftUuid}
                sections={currentDiscoverFeed.sections}
                userSegmentIds={segmentIds || []}
              />
            ) : (
              <Home.LogoContainer>
                <GoatLogo fill="white" height={12} data-qa="overlay_goat_image" />
              </Home.LogoContainer>
            )}
            {isBrandTabEnabled &&
              tabStack?.map((tab, index) => (
                <HomeTab
                  key={tab?.title}
                  halfTabMode={isHalfTabEnabled}
                  title={tab?.title}
                  tabPosition={tabStack?.length - index - 1}
                  noTopPadding={tab?.title !== tabTitles.DISCOVER}
                  tabColor={tabAndTabFontColor?.tabColor || null}
                  textColor={tabAndTabFontColor?.tabFontColor || null}
                >
                  <>
                    {tab?.title === name && (
                      <BrandedContentContainer
                        heroes={brandFeed?.sections || []}
                        draftId={brandFeed?.draftId}
                        draftUuid={brandFeed?.draftUuid}
                        drawerName={name}
                        isVisible={activeTab === name}
                      />
                    )}
                  </>
                </HomeTab>
              ))}
          </>
        )}
      </Home.Container>
    </>
  );
};

export const getServerSideProps = async ({ req, res, locale }) => {
  try {
    const isCrawler = isSearchCrawler(req?.headers);

    let breadcrumbs = {};
    try {
      breadcrumbs = await sneakersRequest(req, res)
        .get(`api/v1/breadcrumbs/homepage`)
        .then((response) => response.body);
    } catch (error) {
      if (typeof error !== 'undefined') {
        bugsnag(error, { context: 'Fetching Homepage Breadcrumb' });
      }
    }

    const discoverTabFeed = await getDiscoverFeed(req);

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        breadcrumbs,
        discoverTabFeed,
        isCrawler,
      },
    };
  } catch (error) {
    if (typeof error !== 'undefined') {
      bugsnag(error, { context: 'Fetching Home Feeds' });
    }
    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
      },
    };
  }
};

Home.Container = styled.div<{ $minHeight?: number }>`
  min-height: ${({ $minHeight }) => ($minHeight > 0 ? `${$minHeight}px` : `100%`)};
  position: relative;
  width: 100%;

  ${hideScrollbars}
`;

Home.LogoContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  height: 100vh;
  width: 100%;
`;

Home.GoatLogo = styled.img`
  height: auto;
  width: 52px;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: auto;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    width: 5vw;
  }
`;

Home.TabWrapper = styled.div`
  display: flex;
  margin: 24px 0 0 16px;
`;

Home.Tab = styled.button<{ $isSelected: boolean }>`
  font-weight: 600;
  ${variableFontRegular({ size: '11.5px', height: '13px' })};
  margin-right: 24px;

  ${({ $isSelected }) =>
    $isSelected &&
    css`
      text-decoration: underline;
      text-underline-offset: 2px;
    `}
`;

export default Home;
